import {
  createContext,
  FC,
  PropsWithChildren,
  ReactEventHandler,
  Reducer,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { App } from "@capacitor/app";
import { useImmerReducer } from "use-immer";
import { useIdleTimer } from "react-idle-timer";

import SongType from "@/types/song-type";
import VocabularyType from "@/types/vocabulary-type";
import StageType from "@/types/stage-type";
import { randomRewardAudioUrl, randomRewardVideoUrl } from "@/utils/rewards";
import { randomRetryPromptUrl } from "@/utils/retry-prompts";
import { isEmpty, sample } from "lodash";
import { request } from "@/utils/request";
import { useAppContext } from "./app-context";
import AssessmentQuestion from "@/types/assessment-question";
import GameplaySession from "@/types/gameplay-session";
import { stageAssetPreloader } from "@/utils/stage-asset-preloader";
import { extractAssetId } from "@/utils/asset-utils";
import { STAGE_NAMES } from "@/constants/stage-names";

type GameContextType = {
  state: GameState;
  input: GameInput;
  controls: GameControls;
  eventHandlers: GameEventHandlers;
};

type GameInput = Record<keyof typeof actions.inputs, () => void>

type GameControls = {
  play: () => void;
  pause: () => void;
  restart: () => void;
  changeStage: (stageNumber: number) => void;
};

type GameEventHandlers = {
  handleEnded: ReactEventHandler<HTMLVideoElement>;
  handleTimeUpdate: ReactEventHandler<HTMLVideoElement>;
  handleVocabularyCueVideoTimeUpdate: ReactEventHandler<HTMLVideoElement>;
  handleAudioEnded: ReactEventHandler<HTMLAudioElement>;
};

type InternalState = {
  phase:
    | "pre_assessment_prompt"
    | "post_assessment_prompt"
    | "prompt"
    | "playback"
    | "input"
    | "reward"
    | "retry_prompt"
    | "solution_playback"
    | "audio_hint_playback"
    | "stage_complete";
  stageNumber: number;
  assessmentQuestionNumber: number,
  retryCount: number;
  playing: boolean;
  vocabularyCueIndex: number;
  vocabularyCueVideoIndex: number;
  lastAction?: GameAction["type"];
  lastAssessmentQuestionId?: number;
  lastAssessmentPhase?: "pre_assessment" | "post_assessment";
  sessionId?: number;
};

type GameState = InternalState & {
  song: SongType;
  stage?: StageType;
  vocabulary?: VocabularyType;
  assessmentQuestion?: AssessmentQuestion;
  videoUrl?: string;
  videoStartTime?: number;
  controlVideoUrl?: string;
  instrumentalAudioUrl?: string;
  audioUrl?: string;
  text?: string;
};

type GameAction =
  | {
      type:
        | "input_yes"
        | "input_no"
        | "input_next"
        | "input_replay"
        | "input_no_response"
        | "input_incorrect"
        | "input_approximation"
        | "input_correct"
        | "input_skip_assessment"
        | "input_skip_stage"
        | "controls_play"
        | "controls_pause"
        | "controls_restart"
        | "gameplay_prompt_ended"
        | "gameplay_playback_ended"
        | "gameplay_reward_ended"
        | "gameplay_retry_prompt_ended"
        | "gameplay_solution_playback_ended"
        | "gameplay_audio_hint_playback_ended"
        | "gameplay_vocabulary_cue"
        | "gameplay_vocabulary_cue_video_ended";
    }
  | {
      type: "controls_change_stage";
      payload: StageType["number"];
    }
  | {
      type: "gameplay_session_started";
      payload?: GameplaySession["id"];
    };

type GameProviderProps = {
  song: SongType;
  initialPhase?: InternalState["phase"];
  initialStageNumber?: number;
  assessmentQuestionNumber?: number;
  assessmentEnabled?: boolean;
};

const GameContext = createContext<GameContextType | null>(null);

const actions = {
  inputs: {
    yes: "input_yes",
    no: "input_no",
    next: "input_next",
    replay: "input_replay",
    noResponse: "input_no_response",
    correct: "input_correct",
    incorrect: "input_incorrect",
    approximation: "input_approximation",
    skipAssessment: "input_skip_assessment",
    skipStage: "input_skip_stage",
  } as const,

  controls: {
    play: "controls_play",
    pause: "controls_pause",
    restart: "controls_restart",
    changeStage: "controls_change_stage",
  } as const,

  gameplay: {
    promptEnded: "gameplay_prompt_ended",
    playbackEnded: "gameplay_playback_ended",
    rewardEnded: "gameplay_reward_ended",
    retryPromptEnded: "gameplay_retry_prompt_ended",
    solutionPlaybackEnded: "gameplay_solution_playback_ended",
    audioHintPlaybackEnded: "gameplay_audio_hint_playback_ended",
    vocabularyCue: "gameplay_vocabulary_cue",
    vocabularyCueVideoEnded: "gameplay_vocabulary_cue_video_ended",
    sessionStarted: "gameplay_session_started"
  } as const,
} as const;

/**
 * Check if the current stage is a custom song singing stage
 */
const isCustomSongSingStage = (song: SongType, stage?: StageType): boolean => {
  return song.isCustom === true && (
    stage?.name === STAGE_NAMES.SING_AND_INSTRUMENT ||
    stage?.name === STAGE_NAMES.SING_VOICE_ONLY ||
    stage?.name === STAGE_NAMES.SING_VOICE_ONLY_REPEAT
  );
};

export const GameProvider: FC<PropsWithChildren<GameProviderProps>> = ({
  song,
  assessmentQuestionNumber = 1,
  assessmentEnabled = false,
  initialStageNumber = 1,
  initialPhase = "prompt",
  children,
}) => {
  const reducer: Reducer<InternalState, GameAction> = (state, action) => {
    const stage = song.stages.find(
      (stage) => stage.number === state.stageNumber,
    );

    switch (action.type) {
      case actions.controls.play:
        state.playing = true;
        break;
      case actions.controls.pause:
        state.playing = false;
        break;
      case actions.controls.restart:
        state.stageNumber = 1;
        state.playing = true;
        state.retryCount = 0;
        state.vocabularyCueIndex = 0;
        state.vocabularyCueVideoIndex = 0;
        state.phase = "prompt";
        break;
      case actions.controls.changeStage: {
        state.stageNumber = action.payload;
        state.playing = true;
        state.retryCount = 0;

        const nextStage = song.stages.find(
          (stage) => stage.number === state.stageNumber,
        );

        state.phase = nextStage?.promptVideo.url ? "prompt" : "playback";
        state.vocabularyCueIndex = 0;
        state.vocabularyCueVideoIndex = 0;

        // Preload assets for the current stage (fire and forget)
        stageAssetPreloader.preloadStage(nextStage, song).catch(error => {
          console.error('Error preloading stage assets:', error);
        });
        break;
      }

      case actions.inputs.yes:
      case actions.inputs.next:
        state.phase = "reward";
        state.playing = true;
        state.retryCount = 0;
        state.vocabularyCueIndex = 0;
        state.vocabularyCueVideoIndex = 0;
        break;
      case actions.inputs.no:
        state.phase = "retry_prompt";
        state.retryCount += 1;
        state.playing = true;

        // For custom songs, don't auto-advance after 2 retries - show skip button instead
        if (!song.isCustom && !isEmpty(stage?.hintAudios)) {
          // For regular songs, check retry count with hintAudios
          if (state.retryCount >= 4) {
            state.stageNumber += 1;

            const nextStage = song.stages.find(
              (stage) => stage.number === state.stageNumber,
            );

            state.phase = nextStage?.promptVideo.url ? "prompt" : "playback";

            state.retryCount = 0;
          } else if (state.retryCount >= 2) {
            // Show audio hint for non-custom songs
            state.phase = "audio_hint_playback";
            state.playing = false;
          }
        }

        break;
      case actions.inputs.skipStage:
        // Skip to next stage (for custom songs after 2 retries)
        state.stageNumber += 1;

        const nextStage = song.stages.find(
          (stage) => stage.number === state.stageNumber,
        );

        state.phase = nextStage?.promptVideo.url ? "prompt" : "playback";
        state.retryCount = 0;
        state.playing = true;
        state.vocabularyCueIndex = 0;
        state.vocabularyCueVideoIndex = 0;

        // Preload assets for the next stage
        stageAssetPreloader.preloadStage(nextStage, song);

        break;
      case actions.inputs.replay:
        state.phase = "prompt";
        state.playing = true;
        state.vocabularyCueIndex = 0;
        state.vocabularyCueVideoIndex = 0;
        break;
      case actions.inputs.noResponse:
      case actions.inputs.incorrect:
      case actions.inputs.approximation:
      case actions.inputs.correct: {
        const lastAssessmentQuestion = song.assessmentQuestions.find(
          (question) => question.number === state.assessmentQuestionNumber
        );

        state.lastAssessmentQuestionId = lastAssessmentQuestion?.id;
        state.lastAssessmentPhase = state.phase === "pre_assessment_prompt" ? "pre_assessment" : "post_assessment";
        state.assessmentQuestionNumber += 1;

        const assessmentQuestion = song.assessmentQuestions.find(
          (question) => question.number === state.assessmentQuestionNumber
        );

        if (!assessmentQuestion) {
          if (state.phase === "post_assessment_prompt") {
            state.phase = "stage_complete";
            state.playing = false;
          } else {
            state.phase = "prompt";
            state.playing = true;
            state.vocabularyCueIndex = 0;
            state.vocabularyCueVideoIndex = 0;
          }
        }
        break;
      }
      case actions.inputs.skipAssessment: {
        if (state.phase === "pre_assessment_prompt") {
          state.phase = "prompt";
          state.playing = true;
          state.vocabularyCueIndex = 0;
          state.vocabularyCueVideoIndex = 0;
        } else if (state.phase === "post_assessment_prompt") {
          state.phase = "stage_complete";
          state.playing = false;
        }
        break;
      }

      case actions.gameplay.vocabularyCue: {
        state.vocabularyCueIndex += 1;
        break;
      }
      case actions.gameplay.vocabularyCueVideoEnded: {
        state.vocabularyCueVideoIndex += 1;
        break;
      }
      case actions.gameplay.promptEnded:
        state.phase = "playback";
        state.vocabularyCueVideoIndex = 0;
        break;
      case actions.gameplay.retryPromptEnded:
        // For custom songs, always go to playback after retry prompt ends
        if (song.isCustom) {
          state.phase = "playback";
        } else if (state.retryCount >= 2 && isEmpty(stage?.hintAudios)) {
          // For regular songs without hintAudios, show solution or go back
          if (stage?.solutionVideo.url) {
            state.phase = "solution_playback";
          } else {
            state.stageNumber -= 1;

            const previousStage = song.stages.find(
              (stage) => stage.number === state.stageNumber,
            );

            state.phase = previousStage?.promptVideo.url
              ? "prompt"
              : "playback";
            state.retryCount = 0;
          }
        } else {
          state.phase = "playback";
        }
        break;
      case actions.gameplay.playbackEnded:
        state.phase = "input";
        state.playing = false;
        break;
      case actions.gameplay.rewardEnded: {
        state.stageNumber += 1;

        if (state.stageNumber > song.stages.length) {
          if (assessmentEnabled) {
            state.phase = "post_assessment_prompt";
            state.assessmentQuestionNumber = 1;
          } else {
            state.phase = "stage_complete";
            state.playing = false;
          }
        } else {
          const nextStage = song.stages.find(
            (stage) => stage.number === state.stageNumber,
          );

          state.phase = nextStage?.promptVideo.url ? "prompt" : "playback";
          state.vocabularyCueIndex = 0;
          state.vocabularyCueVideoIndex = 0;

          // Preload assets for the next stage
          stageAssetPreloader.preloadStage(nextStage, song);
        }
        break;
      }
      case actions.gameplay.solutionPlaybackEnded:
        state.phase = "prompt";
        break;
      case actions.gameplay.audioHintPlaybackEnded:
        state.phase = "input";
        break;
      case actions.gameplay.sessionStarted:
        state.sessionId = action.payload;
        break;
      default:
        break;
    }

    state.lastAction = action.type;

    return state;
  };

  const [state, dispatch] = useImmerReducer<InternalState, GameAction>(
    reducer,
    {
      phase: initialPhase,
      stageNumber: initialStageNumber,
      assessmentQuestionNumber,
      retryCount: 0,
      playing: true,
      vocabularyCueIndex: 0,
      vocabularyCueVideoIndex: 0,
    },
  );

  const stage = useMemo(
    () => song.stages.find((stage) => stage.number === state.stageNumber),
    [song, state.stageNumber],
  );

  const assessmentQuestion = useMemo(() => {
    return song.assessmentQuestions.find(
      (question) => question.number === state.assessmentQuestionNumber
    );
  }, [song.assessmentQuestions, state.assessmentQuestionNumber]);

  const vocabulary = useMemo(() => {
    switch (state.phase) {
      case "pre_assessment_prompt":
      case "post_assessment_prompt":
        return song.vocabularies.find((vocabulary) => vocabulary.id === assessmentQuestion?.vocabularyId);
      default:
        return song.vocabularies.find(
          (vocabulary) =>
            vocabulary.id ==
            stage?.vocabularyCues[state.vocabularyCueIndex].vocabularyId,
        );
    }
  }, [song, stage, state.phase, assessmentQuestion?.vocabularyId, state.vocabularyCueIndex]);

  // Store previous videoUrl and videoId to avoid unnecessary changes when URL signature changes
  const prevVideoUrlRef = useRef<{ url: string | undefined; videoId: string | undefined }>({
    url: undefined,
    videoId: undefined,
  });

  const videoUrl = useMemo(() => {
    // Extract videoId from URL (filename part, ignoring signatures/tokens)
    // This prevents unnecessary re-renders when URLs change due to signature updates
    // See asset-utils.ts for detailed explanation
    const extractVideoId = extractAssetId;

    let newUrl: string | undefined;
    switch (state.phase) {
      case "prompt":
        newUrl = stage?.promptVideo.url;
        break;
      case "playback":
        // For custom songs with singing stages, display vocabulary cue videos in the top-left corner
        if (isCustomSongSingStage(song, stage) && stage) {
          const vocabularyCues = stage.vocabularyCues || [];
          // Filter vocabulary cues that have video URLs
          const videoCues = vocabularyCues.filter(cue => cue.video && cue.video.trim() !== "");
          if (state.vocabularyCueVideoIndex < videoCues.length) {
            newUrl = videoCues[state.vocabularyCueVideoIndex].video;
          } else {
            newUrl = stage.video.url;
          }
        } else {
          newUrl = stage?.video.url;
        }
        break;
      case "reward":
        newUrl = randomRewardVideoUrl();
        break;
      case "retry_prompt":
        newUrl = randomRetryPromptUrl();
        break;
      case "solution_playback":
        newUrl = stage?.solutionVideo.url;
        break;
    }

    // If new URL's videoId matches previous videoId, return previous URL to avoid unnecessary changes
    const newVideoId = newUrl ? extractVideoId(newUrl) : undefined;
    if (newVideoId && prevVideoUrlRef.current.videoId === newVideoId) {
      return prevVideoUrlRef.current.url;
    }

    // Update ref with new URL and videoId
    prevVideoUrlRef.current = {
      url: newUrl,
      videoId: newVideoId,
    };

    return newUrl;
  }, [song, stage, state.phase, state.vocabularyCueVideoIndex]);

  const videoStartTime = useMemo(() => {
    return undefined;
  }, []);

  const controlVideoUrl = useMemo(() => {
    // For custom songs with singing stages, use stage.video to control timing
    if (state.phase === "playback" && isCustomSongSingStage(song, stage)) {
      return stage?.video.url;
    }
    return undefined;
  }, [song, stage, state.phase]);

  const instrumentalAudioUrl = useMemo(() => {
    // For custom songs with "Sing and Instrument" stage during playback phase, play instrumental audio
    if (state.phase === "playback" && song.isCustom && stage?.name === STAGE_NAMES.SING_AND_INSTRUMENT) {
      return song.instumentalAudio?.url;
    }
    return undefined;
  }, [song, stage, state.phase]);

  const audioUrl = useMemo(() => {
    switch (state.phase) {
      case "reward":
        return randomRewardAudioUrl();
      case "audio_hint_playback":
        return sample(stage?.hintAudios.map((audio) => audio.url));
    }
  }, [stage, state.phase]);

  const handleTimeUpdate: ReactEventHandler<HTMLVideoElement> = (event) => {
    const { phase, vocabularyCueIndex } = state;

    if (phase !== "playback") return;
    if (!stage) return;

    const vocabularyCue = stage.vocabularyCues[vocabularyCueIndex + 1];

    const passedCurrentCue =
      vocabularyCue &&
      event.currentTarget.currentTime >=
        vocabularyCue.timestampMilliseconds / 1000;

    if (passedCurrentCue) dispatch({ type: "gameplay_vocabulary_cue" });
  };

  const handleEnded: ReactEventHandler<HTMLVideoElement> = () => {
    switch (state.phase) {
      case "prompt":
        return dispatch({ type: actions.gameplay.promptEnded });
      case "playback":
        // For custom songs with singing stages, check if there's a next vocabulary cue video
        if (isCustomSongSingStage(song, stage) && stage) {
          const vocabularyCues = stage.vocabularyCues || [];
          // Filter vocabulary cues that have video URLs
          const videoCues = vocabularyCues.filter(cue => cue.video && cue.video.trim() !== "");
          if (state.vocabularyCueVideoIndex < videoCues.length - 1) {
            // There's another video, play the next one
            return dispatch({ type: actions.gameplay.vocabularyCueVideoEnded });
          }
        }
        // No more videos, end playback normally
        return dispatch({ type: actions.gameplay.playbackEnded });
      case "retry_prompt":
        return dispatch({ type: actions.gameplay.retryPromptEnded });
      case "solution_playback":
        return dispatch({ type: actions.gameplay.solutionPlaybackEnded });
    }
  };

  const handleVocabularyCueVideoTimeUpdate: ReactEventHandler<HTMLVideoElement> = () => {
    // Placeholder for future time-based video switching logic if needed
  };

  const handleAudioEnded: ReactEventHandler<HTMLAudioElement> = () => {
    switch (state.phase) {
      case "audio_hint_playback":
        return dispatch({ type: actions.gameplay.audioHintPlaybackEnded });
    }
  };

  useEffect(() => {
    if (state.phase === "reward") {
      setTimeout(() => {
        dispatch({ type: actions.gameplay.rewardEnded });
      }, 4_500);
    }
  }, [state.phase, dispatch]);

  const {
    state: { selectedChildId },
  } = useAppContext();

  useEffect(() => {
    switch (state.lastAction) {
      case actions.inputs.yes:
      case actions.inputs.no:
      case actions.inputs.next:
      case actions.inputs.replay:
      case actions.inputs.skipStage:
      case actions.controls.play:
      case actions.controls.pause:
      case actions.controls.restart:
      case actions.controls.changeStage:
        request.post("/activities", {
          gameplayActivity: {
            action: state.lastAction.replaceAll("_", "-"),
            songId: song.id,
            stageId: stage?.id,
            childId: selectedChildId,
          },
        });
        break;
      case actions.inputs.noResponse:
      case actions.inputs.incorrect:
      case actions.inputs.approximation:
      case actions.inputs.correct: {
        request.post("/assessment_question_answers", {
          assessmentQuestionAnswer: {
            assessmentQuestionId: state.lastAssessmentQuestionId,
            phase: state.lastAssessmentPhase,
            value: state.lastAction.substring(6),
            childId: selectedChildId,
          },
        })
        break;
      }
    }
  }, [state.lastAction, song.id, stage?.id, state.lastAssessmentQuestionId, state.lastAssessmentPhase, selectedChildId]);

  useEffect(() => {
    request.post("/activities", {
      gameplayActivity: {
        action: "begin",
        songId: song.id,
        childId: selectedChildId,
      },
    });
  }, [selectedChildId, song.id])

  const { getTotalActiveTime } = useIdleTimer({ timeout: 300_000 })

  // Active time already elapsed when the current session began. Durations are
  // reported as a delta from this, since getTotalActiveTime() counts from when
  // this provider mounted rather than from the start of the session.
  const activeTimeBaselineRef = useRef(0);
  const sessionEndedRef = useRef(false);

  // camelizeKeys rebuilds `song` on every render, so `song.stages` is a new
  // array each time and can never be used as an effect dependency. Derive a
  // scalar that compares by value instead.
  const startStageId = useMemo(
    () => song.stages.find((s) => s.number === initialStageNumber)?.id,
    [song.stages, initialStageNumber],
  );

  const startedSessionKeyRef = useRef<string>();

  useEffect(() => {
    const sessionKey = `${song.id}:${selectedChildId}`;
    if (startedSessionKeyRef.current === sessionKey) return;
    startedSessionKeyRef.current = sessionKey;

    activeTimeBaselineRef.current = getTotalActiveTime();
    sessionEndedRef.current = false;

    request.post<GameplaySession>("/gameplay_sessions", {
      gameplaySession: {
        songId: song.id,
        childId: selectedChildId,
        startStageId,
      },
    }).then(response => {
      dispatch({ type: actions.gameplay.sessionStarted, payload: response.data?.id });
    })
  }, [selectedChildId, song.id, startStageId, getTotalActiveTime, dispatch])

  // Preload initial stage on mount (track to avoid duplicate preloading)
  const initialStagePreloadedRef = useRef(false);
  useEffect(() => {
    if (initialStagePreloadedRef.current) return;
    
    const initialStage = song.stages.find(s => s.number === initialStageNumber);
    if (initialStage) {
      stageAssetPreloader.preloadStage(initialStage, song).catch(error => {
        console.error('Error preloading initial stage assets:', error);
      });
      initialStagePreloadedRef.current = true;
    }
  }, [song, initialStageNumber]);

  // Read through refs at teardown time so this effect can depend on nothing and
  // therefore only run its cleanup when the player actually leaves. Depending on
  // stage or session id would end the session on every stage change instead.
  const sessionIdRef = useRef(state.sessionId);
  const stageIdRef = useRef(stage?.id);
  const getTotalActiveTimeRef = useRef(getTotalActiveTime);

  sessionIdRef.current = state.sessionId;
  stageIdRef.current = stage?.id;
  getTotalActiveTimeRef.current = getTotalActiveTime;

  useEffect(() => {
    const endSession = () => {
      if (!sessionIdRef.current || sessionEndedRef.current) return;
      sessionEndedRef.current = true;

      const durationInSeconds = Math.max(
        0,
        Math.floor(
          (getTotalActiveTimeRef.current() - activeTimeBaselineRef.current) / 1000,
        ),
      );

      request.post(
        `/gameplay_sessions/${sessionIdRef.current}/end`,
        {
          gameplaySession: {
            endStageId: stageIdRef.current,
            durationInSeconds,
          },
        },
        { keepalive: true }
      );
    }

    window.addEventListener("beforeunload", endSession);

    return () => {
      window.removeEventListener("beforeunload", endSession);
      App.removeAllListeners()

      endSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Preload next stage when current stage enters playback phase
  const prevStageNumberRef = useRef<number | undefined>(undefined);
  const nextStagePreloadedRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Only preload next stage when entering playback phase
    if (state.phase === "playback") {
      // Check if we've already preloaded next stage for this stage number
      if (nextStagePreloadedRef.current !== state.stageNumber) {
        const nextStage = song.stages.find(
          (s) => s.number === state.stageNumber + 1
        );
        if (nextStage) {
          stageAssetPreloader.preloadStage(nextStage, song).catch(error => {
            console.error('Error preloading next stage assets:', error);
          });
          nextStagePreloadedRef.current = state.stageNumber;
        }
      }
    }

    // Reset when stage number changes
    if (prevStageNumberRef.current !== state.stageNumber) {
      nextStagePreloadedRef.current = undefined;
      prevStageNumberRef.current = state.stageNumber;
    }
    // song is stable for the component lifecycle, safe to use without dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.stageNumber]);

  // Cleanup preloaded assets on component unmount
  useEffect(() => {
    return () => {
      stageAssetPreloader.clear();
    };
  }, []);

  return (
    <GameContext.Provider
      value={{
        state: {
          ...state,
          song,
          stage,
          vocabulary,
          assessmentQuestion,
          videoUrl,
          videoStartTime,
          controlVideoUrl,
          instrumentalAudioUrl,
          audioUrl,
        },
        input: {
          yes: () => dispatch({ type: actions.inputs.yes }),
          no: () => dispatch({ type: actions.inputs.no }),
          next: () => dispatch({ type: actions.inputs.next }),
          replay: () => dispatch({ type: actions.inputs.replay }),
          noResponse: () => dispatch({ type: actions.inputs.noResponse }),
          incorrect: () => dispatch({ type: actions.inputs.incorrect }),
          approximation: () => dispatch({ type: actions.inputs.approximation }),
          correct: () => dispatch({ type: actions.inputs.correct }),
          skipAssessment: () => dispatch({ type: actions.inputs.skipAssessment }),
          skipStage: () => dispatch({ type: actions.inputs.skipStage }),
        },
        controls: {
          play: () => dispatch({ type: actions.controls.play }),
          pause: () => dispatch({ type: actions.controls.pause }),
          restart: () => dispatch({ type: actions.controls.restart }),
          changeStage: (stageNumber: number) =>
            dispatch({
              type: actions.controls.changeStage,
              payload: stageNumber,
            }),
        },
        eventHandlers: {
          handleEnded,
          handleTimeUpdate,
          handleVocabularyCueVideoTimeUpdate,
          handleAudioEnded,
        },
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGameContext = () => {
  const gameContext = useContext(GameContext);

  return gameContext as GameContextType;
};
